import { useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/auth/AuthContext";
import { isReallyChitty } from "../../shared/util/branding";

const navItems = [
  { label: "Home", destination: "/" },
  { label: "Features", destination: "/features" },
  ...(isReallyChitty() ? [{ label: "Develop", destination: "/develop" }] : []),
  { label: "Games", destination: "/game" },
  { label: "Contact", destination: "/contact" },
];

function NavItem({
  label,
  destination,
}: {
  label: string;
  destination: string;
}) {
  const theme = useTheme();
  const location = useLocation();
  const isActive =
    location.pathname === destination ||
    (destination !== "/" && location.pathname.startsWith(destination));

  return (
    <Link style={{ textDecoration: "none" }} to={destination}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          cursor: "pointer",
          color: isActive ? theme.palette.info.main : theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "1rem",
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": {
            color: theme.palette.info.main,
          },
          "&::after": isActive
            ? {
                content: '""',
                position: "absolute",
                bottom: -4,
                left: "50%",
                transform: "translateX(-50%)",
                width: "60%",
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 2,
              }
            : {},
        }}
      >
        {label}
      </Box>
    </Link>
  );
}

interface NavBarProps {
  user: { displayName?: string; username: string } | null;
  loading: boolean;
}

export function NavBar({ user, loading }: NavBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const handleNavClick = (destination: string) => {
    navigate(destination);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{ color: theme.palette.text.primary, ml: "auto" }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              background: theme.palette.background.paper,
              width: 280,
            },
          }}
        >
          <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ color: theme.palette.text.primary }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <List>
              {navItems.map((item) => {
                const isActive =
                  location.pathname === item.destination ||
                  (item.destination !== "/" &&
                    location.pathname.startsWith(item.destination));
                return (
                  <ListItem key={item.destination} disablePadding>
                    <ListItemButton onClick={() => handleNavClick(item.destination)}>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            color: isActive
                              ? theme.palette.info.main
                              : theme.palette.text.primary,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>

            <Box sx={{ mt: "auto" }}>
              <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", my: 2 }} />
              {user ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      px: 2,
                      pb: 1,
                    }}
                  >
                    {user.displayName ?? user.username}
                  </Typography>
                  <List>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleNavClick("/match")}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <SportsEsportsIcon sx={{ color: theme.palette.text.secondary }} />
                        </ListItemIcon>
                        <ListItemText primary="Matches" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleNavClick("/welcome")}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                        </ListItemIcon>
                        <ListItemText primary="Profile" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton onClick={handleLogout}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <LogoutIcon sx={{ color: theme.palette.text.secondary }} />
                        </ListItemIcon>
                        <ListItemText primary="Log out" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </>
              ) : !loading ? (
                <List>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleNavClick("/login")}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <LoginIcon sx={{ color: theme.palette.text.secondary }} />
                      </ListItemIcon>
                      <ListItemText primary="Log in" />
                    </ListItemButton>
                  </ListItem>
                </List>
              ) : null}
            </Box>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Stack direction={"row"} alignItems="center" spacing={0.5}>
      {navItems.map((item) => (
        <NavItem key={item.destination} label={item.label} destination={item.destination} />
      ))}
    </Stack>
  );
}
